import { createCo2LookupKey } from "@/types/LCAoM/LCAoMService";
import { ModelsDataService } from "@/types/LCAoM/ModelsDataService";
import { AppSettingType, Co2MaterialType, FeatureManagementType, NodeMaterialDto, NodeSupplementalDto, SupplementalType } from "@api/apiClient";
import { normalizeCo2MaterialTypeId } from "@api/co2MaterialTypes";
import { CustomGridTreeDataGroupingCell } from "@components/DataGrid/components/cell/CustomGridTreeDataGroupingCell";
import { GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD, GRID_VISIBILITY_COL_DEF } from "@components/DataGrid/components/colDef/GridVisibilityColDef";
import NullableBooleanColumnFilter from "@components/DataGrid/components/filters/NullableBooleanFilter";
import QuantificationGridHeader from "@components/DataGrid/components/headers/QuantificationGridHeader";
import { getSubSystemNameById, getSystemNameById } from "@features/analysis/utils/metadataEntityUtils";
import i18n from "@i18n/i18n";
import CheckIcon from "@mui/icons-material/Check";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { Theme } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD, GridApi, GridColDef, GridGroupNode, GridRenderCellParams } from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import _ from "lodash";
import { MutableRefObject } from "react";
import { getDynamicColumns } from "../MuiAssignTab/MuiAssignGrid.dynamic-columns";

// TODO: the impactColumns need to be feature flagged based on ADStages
const defaultImpactColumns = ["totalCo2"];
const adStageImpactColumns = ["totalCo2", "a", "b", "c", "a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "b6", "b7", "c1", "c2", "c3", "c4", "d", "biogenic"];

/**
 * Dynamically generates the column configuration for the Assign Grid.
 *
 * This function dynamically generates the columns configuration based
 * on application feature flags and project settings.
 *
 * @param apiRef - MutableRefObject to the GridApi for grid operations
 * @param appStore - Application store containing feature flags, project settings, and lookup methods
 * @param theme - MUI theme object for styling
 * @param co2MaterialTypeSpecKind - Optional kind value for dynamic columns
 * @param modelsDataService - Centralized data service for lookups
 * @param co2MaterialTypeLookup - CO2 material type lookup by ID
 * @param scenarioId - Current scenario ID for lookup key generation
 * @returns Array of GridColDef objects representing all grid columns
 */
export const getColumns = (
  apiRef: MutableRefObject<GridApi>,
  appStore: AppStore,
  theme: Theme,
  co2MaterialTypeSpecKind: string | null,
  modelsDataService: ModelsDataService,
  co2MaterialTypeLookup: Record<string, Co2MaterialType>,
  scenarioId: string,
): GridColDef[] => {
  console.log("Models Data Service:", modelsDataService.nodeMaterials.size);
  console.log("Models Data Service:", modelsDataService.nodeSupplementals.size);
  
  const columns = getDefaultColumns(apiRef, appStore, theme, modelsDataService, co2MaterialTypeLookup, scenarioId);

  const impactColumns = appStore.hasFeature(FeatureManagementType.AssignAllADStages) ? adStageImpactColumns : defaultImpactColumns;

  // All nodes (element, rebar, non-mod) now use the same lookup - each has unique nodeId
  const impactColumnsToInsert = impactColumns.map((col) => getCo2eColumnDef(col, col, modelsDataService, scenarioId));

  // Insert the impact columns before rebarPercentage
  const index = columns.findIndex((col) => col.field === "rebarPercentage");
  columns.splice(index, 0, ...impactColumnsToInsert);

  // If we find Co2MaterialTypeSpecs in Nodes then add dynamic columns based on the Kind value
  const dynamicColumns = getDynamicColumns(co2MaterialTypeSpecKind);
  if (dynamicColumns.length > 0) {
    const dynamicIndex = columns.findIndex((col) => col.field === "cadMaterial");
    columns.splice(dynamicIndex, 0, ...dynamicColumns);
  }

  // For each column that has a valueGetter, assign it to groupingValueGetter as well
  columns.forEach((col) => {
    if (col.valueGetter) {
      col.groupingValueGetter = (...params) => {
        const value = col.valueGetter!(...params);
        if (!value) {
          return "<None>";
        }
        return value;
      };
    }
  });

  console.log("Assign Grid Columns:", columns);
  return columns;
};

const getDefaultColumns = (
  apiRef: MutableRefObject<GridApi>,
  appStore: AppStore,
  theme: Theme,
  modelsDataService: ModelsDataService,
  co2MaterialTypeLookup: Record<string, Co2MaterialType>,
  scenarioId: string,
): GridColDef[] => [
  {
    ...GRID_VISIBILITY_COL_DEF,
    headerName: i18n.t("assign-grid.columns.visibility"),
  },
  {
    field: "assigned",
    headerName: i18n.t("assign-grid.columns.assigned"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.assigned")} />,
    flex: 1,
    type: "boolean",
    valueGetter: (_value: any, row: any) => {
      const { material } = getLookupContext(row, scenarioId, modelsDataService);
      return !!material;
    },
    renderCell: (params) => {
      return params.value ? <ToggleOnIcon sx={{ color: theme?.palette.info.main }} /> : <ToggleOffIcon sx={{ color: theme?.palette.text.disabled }} />;
    },
    renderHeaderFilter: () => (
      <NullableBooleanColumnFilter
        apiRef={apiRef}
        field="assigned"
        labels={{
          none: i18n.t("assign-grid.columns.assigned-filter.none"),
          true: i18n.t("assign-grid.columns.assigned-filter.true"),
          false: i18n.t("assign-grid.columns.assigned-filter.false"),
        }}
      />
    ),
  },
  {
    field: "quantityTotal",
    headerName: i18n.t("assign-grid.columns.quantity-total"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.quantity-total")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => row?.data?.quantity,
    valueFormatter: (value?: number) => {
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  },
  {
    field: "lengthTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.length-total"), unit: i18n.t("units.m") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.length-total")} unit={i18n.t("units.m")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      // When An element contains insulation thickness as a child component length should not be considered
      if (row.data?.cadName?.trim().includes("Insulation") && row.data?.length == 0) {
        return;
      }
      return row?.data?.length;
    },
    valueFormatter: (value: any) => {
      if (_.isFinite(value)) {
        return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
      } else {
        return "-";
      }
    },
  },
  {
    field: "areaTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.area-total"), unit: i18n.t("units.m2") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.area-total")} unit={i18n.t("units.m2")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      // When An element contains insulation thickness as a child component area should not be considered
      if (row.data?.cadName?.trim().includes("Insulation") && row.data?.area == 0) {
        return;
      }
      return row?.data?.area;
    },
    valueFormatter: (value: any) => {
      if (_.isFinite(value)) {
        return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
      } else {
        return "-";
      }
    },
  },
  {
    field: "volumeTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.volume-total"), unit: i18n.t("units.m3") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.volume-total")} unit={i18n.t("units.m3")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => row?.data?.volume,
    valueFormatter: (value?: number) => {
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  },
  {
    field: "systemId",
    headerName: i18n.t("assign-grid.columns.system"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.system")} />,
    flex: 1,
    valueGetter: (_val, row) => row?.data?.systemId || "",
    valueFormatter: (value?: string) => {
      return getSystemNameById(appStore.metadataEntities, value || "");
    },
  },
  {
    field: "subSystemId",
    headerName: i18n.t("assign-grid.columns.sub-system"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.sub-system")} />,
    flex: 1,
    valueGetter: (_val, row) => row?.data?.subSystemId || "",
    valueFormatter: (value?: string) => {
      return getSubSystemNameById(appStore.metadataEntities, value || "");
    },
  },
  {
    field: "attribute1",
    headerName: i18n.t("assign-grid.columns.attribute1"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.attribute1")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.attribute1 ?? row?.data?.freeText1 ?? "",
  },
  {
    field: "attribute2",
    headerName: i18n.t("assign-grid.columns.attribute2"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.attribute2")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.attribute2 ?? row?.data?.freeText2 ?? "",
  },
  {
    field: "attribute3",
    headerName: i18n.t("assign-grid.columns.attribute3"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.attribute3")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.attribute3 ?? row?.data?.freeText3 ?? "",
  },
  {
    field: "multiplierValue",
    headerName: i18n.t("assign-grid.columns.multiplier"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.multiplier")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const value = row?.data?.multiplierValue;
      return typeof value === "number" && !isNaN(value) && value > 0 ? value.toFixed(2) : row?.data?.multiplierInput || "";
    },
  },
  {
    field: "notes",
    headerName: i18n.t("assign-grid.columns.notes"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.notes")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.notes || "",
  },
  {
    field: "carbonImpactValue",
    headerName: i18n.t("assign-grid.columns.carbon-impact"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.carbon-impact")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const value = row?.data?.carbonImpactValue;
      return typeof value === "number" && !isNaN(value) && value > 0 ? value.toFixed(2) : row?.data?.carbonImpactInput || "";
    },
  },
  {
    field: "carbonImpactNotes",
    headerName: i18n.t("assign-grid.columns.carbon-impact-notes"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.carbon-impact-notes")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.carbonImpactNotes || "",
  },
  {
    field: "cadMaterial",
    headerName: i18n.t("assign-grid.columns.cad-material"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-material")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadMaterial;
    },
  },
  // TODO: cleanup these long i18n keys
  //  material-co2-material-type-name, material-co2-material-type-a1A2A3, material-co2-material-type-unit
  {
    field: "material.co2MaterialType.name",
    headerName: i18n.t("assign-grid.columns.material-co2-material-type-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.material-co2-material-type-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const { material } = getLookupContext(row, scenarioId, modelsDataService);
      const co2MaterialTypeId = normalizeCo2MaterialTypeId(material?.co2MaterialTypeId);
      if (!co2MaterialTypeId) return "";
      return co2MaterialTypeLookup[co2MaterialTypeId]?.name ?? "";
    },
  },
  {
    field: "material.co2MaterialType.a1A2A3",
    headerName: i18n.t("assign-grid.columns.material-co2-material-type-a1A2A3"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.material-co2-material-type-a1A2A3")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const { material } = getLookupContext(row, scenarioId, modelsDataService);
      const co2MaterialTypeId = normalizeCo2MaterialTypeId(material?.co2MaterialTypeId);
      if (!co2MaterialTypeId) return null;
      return co2MaterialTypeLookup[co2MaterialTypeId]?.a1A2A3 ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  },
  {
    field: "material.co2MaterialType.unit",
    headerName: i18n.t("assign-grid.columns.material-co2-material-type-unit"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.material-co2-material-type-unit")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const { material } = getLookupContext(row, scenarioId, modelsDataService);
      const co2MaterialTypeId = normalizeCo2MaterialTypeId(material?.co2MaterialTypeId);
      if (!co2MaterialTypeId) return "";
      return co2MaterialTypeLookup[co2MaterialTypeId]?.unit ?? "";
    },
  },
  /* A-D Impact Columns will be inserted here ... */
  {
    field: "rebarPercentage",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.rebar.percent"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.rebar.percent")} unit={i18n.t("units.%")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const { supplemental } = getLookupContext(row, scenarioId, modelsDataService);
      if (supplemental?.type === SupplementalType.Rebar) {
        return supplemental.value;
      }
      return null;
    },
    valueFormatter: (value?: number | null) => {
      if (!_.isFinite(value)) return;
      return i18n.t("percent", { value });
    },
  },
  {
    field: "nonModPercentage",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.non-mod.percent"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.non-mod.percent")} unit={i18n.t("units.%")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const { supplemental } = getLookupContext(row, scenarioId, modelsDataService);
      if (supplemental?.type === SupplementalType.NonModelled) {
        return supplemental.value;
      }
      return null;
    },
    valueFormatter: (value?: number | null) => {
      if (!_.isFinite(value)) return;
      return i18n.t("percent", { value });
    },
  },
  {
    field: "source",
    headerName: i18n.t("assign-grid.columns.data-source"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.data-source")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const { material } = getLookupContext(row, scenarioId, modelsDataService);
      const co2MaterialTypeId = normalizeCo2MaterialTypeId(material?.co2MaterialTypeId);
      if (!co2MaterialTypeId) return "";
      return co2MaterialTypeLookup[co2MaterialTypeId]?.source ?? "";
    },
  },
  {
    field: "type",
    headerName: i18n.t("assign-grid.columns.type"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.type")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.type;
    },
  },
  {
    field: "key",
    headerName: i18n.t("assign-grid.columns.key"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.key")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.id;
    },
  },
  {
    field: "cadId",
    headerName: i18n.t("assign-grid.columns.cad-id"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-id")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.cadId || "",
  },
  {
    field: "modelName",
    headerName: i18n.t("assign-grid.columns.model-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.model-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const modelId = row?.data?.modelId || row?.modelId;
      return appStore.getModelDisplayName(modelId);
    },
  },
  {
    field: "cadModelCategory",
    headerName: i18n.t("assign-grid.columns.cad-model-category-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-model-category-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const categoryId = row?.data?.cadModelCategoryId;
      const label = appStore.getCadModelCategoryLabel(categoryId);
      return label;
    },
  },
  {
    field: "cadFamily",
    headerName: i18n.t("assign-grid.columns.cad-family"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-family")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadFamily;
    },
  },
  {
    field: "cadName",
    headerName: i18n.t("assign-grid.columns.cad-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadName;
    },
  },
  {
    field: "isStructuralMaterial",
    headerName: i18n.t("assign-grid.columns.is-structural-material"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.is-structural-material")} />,
    flex: 1,
    type: "boolean",
    renderCell: (params: GridRenderCellParams) => {
      return params.row.data?.isStructuralMaterial ? <CheckIcon sx={{ color: theme.palette.success.main }} /> : null;
    },
  },
  {
    field: "siblings",
    headerName: i18n.t("assign-grid.columns.siblings"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.siblings")} />,
    flex: 0,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      if (row.data?.cadName?.trim().includes("Insulation")) {
        return;
      }
      return row?.siblings;
    },
    valueFormatter: (value?: number) => {
      if (_.isFinite(value)) {
        return i18n.t("number", { value, maximumFractionDigits: 0 });
      } else {
        return "";
      }
    },
  },
  {
    field: "scopeId",
    headerName: i18n.t("assign-grid.columns.scope"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.scope")} />,
    flex: 1,
    type: "singleSelect",
    valueGetter: (_value: any, row: any) => {
      return row.data?.scopeId;
    },
    valueFormatter: (value?: number | null) => {
      return value ? appStore.getScopeName(value) : "";
    },
    valueOptions: Object.entries(appStore.getScopeLookupMap()).map(([key, value]) => ({
      value: key,
      label: value.name,
    })),
  },
];

const getLookupContext = (
  row: any,
  scenarioId: string,
  modelsDataService: ModelsDataService,
): { material?: NodeMaterialDto; supplemental?: NodeSupplementalDto } => {
  const nodeId = row?.data?.nodeId ?? row?.nodeId;
  if (nodeId === undefined || nodeId === null) {
    return {};
  }
  // nodeMaterials uses string key format (scenarioId:nodeId)
  const materialKey = createCo2LookupKey(scenarioId, nodeId);
  return {
    material: modelsDataService.nodeMaterials.get(materialKey),
    // nodeSupplementals uses nodeId directly (number key)
    supplemental: modelsDataService.nodeSupplementals.get(nodeId),
  };
};

/**
 * Creates a CO2 column definition based on the column type.
 * All node types (element, rebar, non-mod) use the same lookup since each has a unique nodeId.
 * @param column The column type (e.g., 'a', 'b', 'c', 'totalCo2', 'a1A2A3')
 * @param prop The property name to access on NodeMaterialDto
 * @param modelsDataService Centralized data service for lookups
 * @param scenarioId Current scenario ID for lookup key generation
 */
const getCo2eColumnDef = (column: string, prop: string, modelsDataService: ModelsDataService, scenarioId: string): GridColDef => {
  switch (column) {
    case "a":
      return getACo2eColumnDef(column, modelsDataService, scenarioId);
    case "b":
      return getBCo2eColumnDef(column, modelsDataService, scenarioId);
    case "c":
      return getCCo2eColumnDef(column, modelsDataService, scenarioId);
    default:
      return getDefaultCo2eColumnDef(column, prop, modelsDataService, scenarioId);
  }
};

/**
 * Creates a default CO2 column definition that looks up values from the materials lookup table.
 * @param field Column field name
 * @param prop The property name to access on NodeMaterialDto (e.g., 'a1A2A3', 'totalCo2')
 * @param modelsDataService Centralized data service for lookups
 * @param scenarioId Current scenario ID for lookup key generation
 */
const getDefaultCo2eColumnDef = (field: string, prop: string, modelsDataService: ModelsDataService, scenarioId: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const nodeId = row?.data?.nodeId;
      if (nodeId === undefined || nodeId === null) return null;

      const key = createCo2LookupKey(scenarioId, nodeId);
      const material = modelsDataService.nodeMaterials.get(key);

      return material?.[prop as keyof NodeMaterialDto] ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

/**
 * Creates an aggregate A-stage (A1-A3 + A4 + A5) CO2 column definition.
 * @param field Column field name
 * @param modelsDataService Centralized data service for lookups
 * @param scenarioId Current scenario ID for lookup key generation
 */
const getACo2eColumnDef = (field: string, modelsDataService: ModelsDataService, scenarioId: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const nodeId = row?.data?.nodeId;
      if (nodeId === undefined || nodeId === null) return null;

      const key = createCo2LookupKey(scenarioId, nodeId);
      const material = modelsDataService.nodeMaterials.get(key);
      if (!material) return null;

      const { a1A2A3, a4, a5 } = material;
      if (a1A2A3 == null && a4 == null && a5 == null) {
        return null;
      }

      return (a1A2A3 || 0) + (a4 || 0) + (a5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

/**
 * Creates an aggregate B-stage (B1-B7) CO2 column definition.
 * @param field Column field name
 * @param modelsDataService Centralized data service for lookups
 * @param scenarioId Current scenario ID for lookup key generation
 */
const getBCo2eColumnDef = (field: string, modelsDataService: ModelsDataService, scenarioId: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const nodeId = row?.data?.nodeId;
      if (nodeId === undefined || nodeId === null) return null;

      const key = createCo2LookupKey(scenarioId, nodeId);
      const material = modelsDataService.nodeMaterials.get(key);
      if (!material) return null;

      const { b1, b2, b3, b4, b5, b6, b7 } = material;
      if (b1 == null && b2 == null && b3 == null && b4 == null && b5 == null && b6 == null && b7 == null) {
        return null;
      }

      return (b1 || 0) + (b2 || 0) + (b3 || 0) + (b4 || 0) + (b5 || 0) + (b6 || 0) + (b7 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

/**
 * Creates an aggregate C-stage (C1-C4) CO2 column definition.
 * @param field Column field name
 * @param modelsDataService Centralized data service for lookups
 * @param scenarioId Current scenario ID for lookup key generation
 */
const getCCo2eColumnDef = (field: string, modelsDataService: ModelsDataService, scenarioId: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const nodeId = row?.data?.nodeId;
      if (nodeId === undefined || nodeId === null) return null;

      const key = createCo2LookupKey(scenarioId, nodeId);
      const material = modelsDataService.nodeMaterials.get(key);
      if (!material) return null;

      const { c1, c2, c3, c4 } = material;
      if (c1 == null && c2 == null && c3 == null && c4 == null) {
        return null;
      }

      return (c1 || 0) + (c2 || 0) + (c3 || 0) + (c4 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getGroupingColumnDef = (headerName: string): GridColDef => {
  return {
    field: "name",
    headerName: headerName,
    renderHeader: () => <QuantificationGridHeader name={headerName} />,
    filterable: true,
    hideable: false,
    valueGetter: (_rawValue: any, row: any, _colDef: any) => {
      //console.log("grouping valueGetter rawValue:", rawValue, "row:", row, "colDef:", colDef);
      return row?.label;
    },
    renderCell: (params) => {
      //console.log("grouping renderCell params:", params);
      return <CustomGridTreeDataGroupingCell {...(params as GridRenderCellParams<any, any, any, GridGroupNode>)} hideDescendantCount={true} />;
    },
  };
};

const generateColumns = (srcArr: string[], addArr: string[], prefix: string, insertAfter: string): string[] => {
  const newArr = [];
  const insertIndex = srcArr.indexOf(insertAfter);
  if (insertIndex === -1) {
    newArr.push(...srcArr);
    newArr.push(...addArr.map((col) => `${prefix}${col}`));
  } else {
    for (let i = 0; i < srcArr.length; i++) {
      if (i === insertIndex) {
        newArr.push(srcArr[i]);
        newArr.push(...addArr.map((col) => `${prefix}${col}`));
      } else {
        newArr.push(srcArr[i]);
      }
    }
  }
  return newArr;
};

export const getFilteredColumns = (
  columns: GridColDef[],
  appStore: any,
  _profileLevelRebarSettings: boolean = false,
  _project?: { rebarEstimationStatus?: any; nonModelledElementsStatus?: any },
): { filteredColumns: GridColDef[]; columnVisibilityModel: Record<string, boolean> } => {
  let filteredColumns = [...columns];
  let defaultVisibleColumns = [
    GRID_CHECKBOX_SELECTION_FIELD,
    GRID_TREE_DATA_GROUPING_FIELD,

    "label",
    GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD,
    "quantityTotal",
    "lengthTotal",
    "areaTotal",
    "volumeTotal",
    "systemId",
    "subSystemId",
    "attribute1",
    "attribute2",
    "attribute3",
    "modelName",
    "cadMaterial",
    "material.co2MaterialType.name",
    "material.co2MaterialType.a1A2A3",
    "material.co2MaterialType.unit",
    "totalCo2",
    "a",
    "b",
    "c",
    "a1A2A3",
    "a4",
    "a5",
    "b1",
    "b2",
    "b3",
    "b4",
    "b5",
    "b6",
    "b7",
    "c1",
    "c2",
    "c3",
    "c4",
    "d",
    "biogenic",
    "rebarPercentage",
    "nonModPercentage",
  ];
  const impactColumns = appStore.hasFeature(FeatureManagementType.AssignAllADStages) ? adStageImpactColumns : defaultImpactColumns;
  const scopeColumns = ["scopeId", "scoped"];

  // TODO: ADStages needs to be more granular, but for now we just check if the feature is enabled
  if (appStore.hasFeature(FeatureManagementType.AssignAllADStages)) {
    defaultVisibleColumns = generateColumns(defaultVisibleColumns, impactColumns, "", "notes");
  }
  if (!appStore.hasFeature(FeatureManagementType.AttributesVisibility)) {
    filteredColumns = filteredColumns.filter((c) => c.field !== "systemId" && c.field !== "subSystemId");
  }
  if (!appStore.hasFeature(FeatureManagementType.ModelScopes)) {
    filteredColumns = filteredColumns.filter((c) => !scopeColumns.includes(c.field));
  }

  // Generate column visibility model
  const columnVisibilityModel = filteredColumns.reduce<Record<string, boolean>>((acc, col) => {
    acc[col.field] = defaultVisibleColumns.includes(col.field); // Set true for defaultColumns, false for others
    return acc;
  }, {});

  return { filteredColumns, columnVisibilityModel };
};
